import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';
import type {
  BotAnalyticsKpis,
  BotBlockedPhoneRow,
  BotConversationRow,
  BotMessageRow,
  BotMessageTemplateRow,
  BotScoringRuleRow,
  BotState,
  BotStateTransitionRow,
  ConversationFilters,
  LeadActionAppendNoteResponse,
  LeadActionChangeStatusResponse,
  LeadActionConvertResponse,
  LifecycleLeadStatus,
  ServiceSlug,
  TemplateCategory,
} from './bot.schema';
import { BOT_STATES } from './bot.schema';

// ─── conversations ────────────────────────────────────────────────────────

export const conversationsQueryKey = (orgId: string | null, filters?: ConversationFilters) =>
  ['bot-conversations', orgId, filters] as const;

async function fetchConversations(orgId: string, filters: ConversationFilters): Promise<BotConversationRow[]> {
  let query = supabase
    .from('bot_conversations')
    .select('*')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('last_message_at', { ascending: false })
    .limit(200);

  if (!filters.showDormant) {
    query = query.neq('state', 'DORMANT');
  }
  if (filters.states.length > 0) {
    query = query.in('state', filters.states);
  }
  if (filters.serviceSlugs.length > 0) {
    const withValue = filters.serviceSlugs.filter((v): v is ServiceSlug => v !== null);
    const hasNull = filters.serviceSlugs.includes(null);
    if (withValue.length > 0 && hasNull) {
      query = query.or(`service_slug.in.(${withValue.join(',')}),service_slug.is.null`);
    } else if (withValue.length > 0) {
      query = query.in('service_slug', withValue);
    } else if (hasNull) {
      query = query.is('service_slug', null);
    }
  }

  const search = filters.search.trim().replace(/[%(),]/g, '');
  if (search) {
    query = query.or(`phone.ilike.%${search}%,full_name.ilike.%${search}%,business_type.ilike.%${search}%`);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data as unknown as BotConversationRow[];
}

export function useBotConversations(orgId: string | null, filters: ConversationFilters) {
  return useQuery({
    queryKey: conversationsQueryKey(orgId, filters),
    queryFn: () => {
      if (!orgId) return Promise.resolve<BotConversationRow[]>([]);
      return fetchConversations(orgId, filters);
    },
    enabled: Boolean(orgId),
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  });
}

async function fetchConversation(orgId: string, id: string): Promise<BotConversationRow | null> {
  const { data, error } = await supabase
    .from('bot_conversations')
    .select('*')
    .eq('organization_id', orgId)
    .eq('id', id)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as BotConversationRow | null);
}

async function fetchConversationByLeadId(orgId: string, leadId: string): Promise<BotConversationRow | null> {
  const { data, error } = await supabase
    .from('bot_conversations')
    .select('*')
    .eq('organization_id', orgId)
    .eq('lead_id', leadId)
    .is('deleted_at', null)
    .order('last_message_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as BotConversationRow | null);
}

export function useBotConversationByLeadId(orgId: string | null, leadId: string | null) {
  return useQuery({
    queryKey: ['bot-conversation-by-lead', orgId, leadId] as const,
    queryFn: () => {
      if (!orgId || !leadId) return Promise.resolve<BotConversationRow | null>(null);
      return fetchConversationByLeadId(orgId, leadId);
    },
    enabled: Boolean(orgId && leadId),
    staleTime: 15 * 1000,
  });
}

export function useBotConversation(orgId: string | null, id: string | null) {
  return useQuery({
    queryKey: ['bot-conversation', orgId, id] as const,
    queryFn: () => {
      if (!orgId || !id) return Promise.resolve<BotConversationRow | null>(null);
      return fetchConversation(orgId, id);
    },
    enabled: Boolean(orgId && id),
    staleTime: 15 * 1000,
  });
}

export function useUpdateConversationState(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, state }: { id: string; state: BotState }) => {
      const { error } = await supabase
        .from('bot_conversations')
        .update({ state })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bot-conversations', orgId] });
      await qc.invalidateQueries({ queryKey: ['bot-conversation', orgId] });
    },
  });
}

// ─── messages ─────────────────────────────────────────────────────────────

async function fetchMessages(orgId: string, convId: string): Promise<BotMessageRow[]> {
  const { data, error } = await supabase
    .from('bot_messages')
    .select('*')
    .eq('organization_id', orgId)
    .eq('conversation_id', convId)
    .order('created_at', { ascending: true })
    .limit(500);
  if (error) throw error;
  return data as unknown as BotMessageRow[];
}

export function useBotMessages(orgId: string | null, convId: string | null) {
  return useQuery({
    queryKey: ['bot-messages', orgId, convId] as const,
    queryFn: () => {
      if (!orgId || !convId) return Promise.resolve<BotMessageRow[]>([]);
      return fetchMessages(orgId, convId);
    },
    enabled: Boolean(orgId && convId),
    staleTime: 15 * 1000,
    refetchInterval: 20 * 1000,
  });
}

// ─── blocked phones ───────────────────────────────────────────────────────

async function fetchBlocked(orgId: string): Promise<BotBlockedPhoneRow[]> {
  const { data, error } = await supabase
    .from('bot_blocked_phones')
    .select('*')
    .eq('organization_id', orgId)
    .order('blocked_at', { ascending: false });
  if (error) throw error;
  return data as unknown as BotBlockedPhoneRow[];
}

export function useBotBlockedPhones(orgId: string | null) {
  return useQuery({
    queryKey: ['bot-blocked', orgId] as const,
    queryFn: () => {
      if (!orgId) return Promise.resolve<BotBlockedPhoneRow[]>([]);
      return fetchBlocked(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
  });
}

export function useBlockPhone(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ phone, reason, notes }: { phone: string; reason: 'manual' | 'spam' | 'stop_keyword'; notes?: string }) => {
      if (!orgId) throw new Error('missing org');
      const { error } = await supabase
        .from('bot_blocked_phones')
        .insert({ organization_id: orgId, phone, reason, notes: notes ?? null });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bot-blocked', orgId] });
    },
  });
}

export function useUnblockPhone(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('bot_blocked_phones').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bot-blocked', orgId] });
    },
  });
}

// ─── message templates ────────────────────────────────────────────────────

async function fetchTemplates(orgId: string): Promise<BotMessageTemplateRow[]> {
  const { data, error } = await supabase
    .from('bot_message_templates')
    .select('*')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data as unknown as BotMessageTemplateRow[];
}

export function useBotMessageTemplates(orgId: string | null) {
  return useQuery({
    queryKey: ['bot-templates', orgId] as const,
    queryFn: () => {
      if (!orgId) return Promise.resolve<BotMessageTemplateRow[]>([]);
      return fetchTemplates(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

interface TemplateInput {
  slug: string;
  category: TemplateCategory | null;
  label_he: string;
  body_he: string;
  is_active: boolean;
  sort_order: number;
}

export function useUpsertTemplate(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }: { id: string | null; values: TemplateInput }) => {
      if (!orgId) throw new Error('missing org');
      if (id) {
        const { error } = await supabase
          .from('bot_message_templates')
          .update(values)
          .eq('id', id);
        if (error) throw error;
        return;
      }
      const { error } = await supabase
        .from('bot_message_templates')
        .insert({ ...values, organization_id: orgId });
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bot-templates', orgId] });
    },
  });
}

export function useSoftDeleteTemplate(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('bot_message_templates')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bot-templates', orgId] });
    },
  });
}

// ─── scoring rules ────────────────────────────────────────────────────────

async function fetchScoringRules(orgId: string): Promise<BotScoringRuleRow[]> {
  const { data, error } = await supabase
    .from('bot_scoring_rules')
    .select('*')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return data as unknown as BotScoringRuleRow[];
}

export function useBotScoringRules(orgId: string | null) {
  return useQuery({
    queryKey: ['bot-scoring', orgId] as const,
    queryFn: () => {
      if (!orgId) return Promise.resolve<BotScoringRuleRow[]>([]);
      return fetchScoringRules(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

export function useUpdateScoringRule(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<Pick<BotScoringRuleRow, 'weight' | 'is_active' | 'label_he' | 'description_he'>> }) => {
      const { error } = await supabase.from('bot_scoring_rules').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['bot-scoring', orgId] });
    },
  });
}

// ─── analytics ────────────────────────────────────────────────────────────

async function fetchAnalytics(orgId: string): Promise<BotAnalyticsKpis> {
  const weekAgoIso = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const [convsRes, msgsRes] = await Promise.all([
    supabase
      .from('bot_conversations')
      .select('id, state, service_slug, fallback_count, created_at')
      .eq('organization_id', orgId)
      .is('deleted_at', null),
    supabase
      .from('bot_messages')
      .select('id, conversation_id, is_fallback, created_at')
      .eq('organization_id', orgId)
      .gte('created_at', weekAgoIso),
  ]);

  if (convsRes.error) throw convsRes.error;
  if (msgsRes.error) throw msgsRes.error;

  const convs = convsRes.data;
  const msgs = msgsRes.data;

  const recentConvs = convs.filter((c) => c.created_at >= weekAgoIso);
  const reachedHandoff = convs.filter((c) => c.state === 'IDLE_AFTER_HANDOFF' || c.state === 'HUMAN_TAKEOVER').length;
  const fallbackMsgs = msgs.filter((m) => m.is_fallback).length;
  const totalMsgs = msgs.length;
  const msgsByConv = new Map<string, number>();
  for (const m of msgs) {
    msgsByConv.set(m.conversation_id, (msgsByConv.get(m.conversation_id) ?? 0) + 1);
  }
  const avgMessagesPerConv = msgsByConv.size > 0
    ? Math.round((Array.from(msgsByConv.values()).reduce((a, b) => a + b, 0) / msgsByConv.size) * 10) / 10
    : 0;

  const serviceMap = new Map<ServiceSlug | null, number>();
  for (const c of convs) {
    const key = (c.service_slug as ServiceSlug | null) ?? null;
    serviceMap.set(key, (serviceMap.get(key) ?? 0) + 1);
  }
  const serviceMix = Array.from(serviceMap.entries())
    .map(([slug, count]) => ({ slug, count }))
    .sort((a, b) => b.count - a.count);

  const stateMap = new Map<BotState, number>();
  for (const state of BOT_STATES) stateMap.set(state, 0);
  for (const c of convs) {
    const s = c.state as BotState;
    stateMap.set(s, (stateMap.get(s) ?? 0) + 1);
  }
  const stateFunnel = Array.from(stateMap.entries()).map(([state, count]) => ({ state, count }));

  return {
    totalConvs7d: recentConvs.length,
    newConvs7d: recentConvs.length,
    reachedHandoff,
    handoffRate: convs.length > 0 ? reachedHandoff / convs.length : 0,
    fallbackRate: totalMsgs > 0 ? fallbackMsgs / totalMsgs : 0,
    avgMessagesPerConv,
    serviceMix,
    stateFunnel,
  };
}

export function useBotAnalytics(orgId: string | null) {
  return useQuery({
    queryKey: ['bot-analytics', orgId] as const,
    queryFn: () => {
      if (!orgId) {
        return Promise.resolve<BotAnalyticsKpis>({
          totalConvs7d: 0,
          newConvs7d: 0,
          reachedHandoff: 0,
          handoffRate: 0,
          fallbackRate: 0,
          avgMessagesPerConv: 0,
          serviceMix: [],
          stateFunnel: [],
        });
      }
      return fetchAnalytics(orgId);
    },
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
  });
}

// ─── state transitions (migration 0013) ───────────────────────────────────

async function fetchTransitionLog(orgId: string, convId: string): Promise<BotStateTransitionRow[]> {
  // `bot_state_transitions` is created by migration 0013 and not yet in the
  // generated database.types.ts (db:types regenerates from remote — Yoav
  // runs that post-merge). Use the untyped REST path via rpc-style call
  // through a minimal SELECT executor that bypasses the generated types.
  /* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment */
  const untypedFrom = (supabase as any).from.bind(supabase) as (t: string) => any;
  const { data, error } = await untypedFrom('bot_state_transitions')
    .select('*')
    .eq('organization_id', orgId)
    .eq('conversation_id', convId)
    .order('created_at', { ascending: true })
    .limit(500);
  /* eslint-enable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment */
  if (error) throw error;
  return (data ?? []) as BotStateTransitionRow[];
}

export function useBotTransitionLog(orgId: string | null, convId: string | null) {
  return useQuery({
    queryKey: ['bot-transitions', orgId, convId] as const,
    queryFn: () => {
      if (!orgId || !convId) return Promise.resolve<BotStateTransitionRow[]>([]);
      return fetchTransitionLog(orgId, convId);
    },
    enabled: Boolean(orgId && convId),
    staleTime: 30 * 1000,
    refetchInterval: 30 * 1000,
  });
}

// (Per-conversation analytics card is PR2; useBotConversationAnalytics lives
// alongside it. PR1 just needs the transition_log fetch above.)

// ─── lifecycle: linked lead lookup ────────────────────────────────────────

export interface LifecycleLeadRow {
  id: string;
  organization_id: string;
  full_name: string;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  interest: string | null;
  status: LifecycleLeadStatus;
  notes: string | null;
  linked_customer_id: string | null;
  linked_quote_id: string | null;
}

async function fetchLifecycleLead(orgId: string, leadId: string): Promise<LifecycleLeadRow | null> {
  const { data, error } = await supabase
    .from('leads')
    .select('id, organization_id, full_name, company_name, email, phone, interest, status, notes, linked_customer_id, linked_quote_id')
    .eq('organization_id', orgId)
    .eq('id', leadId)
    .is('deleted_at', null)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as LifecycleLeadRow | null);
}

export function useLifecycleLead(orgId: string | null, leadId: string | null) {
  return useQuery({
    queryKey: ['lifecycle-lead', orgId, leadId] as const,
    queryFn: () => {
      if (!orgId || !leadId) return Promise.resolve<LifecycleLeadRow | null>(null);
      return fetchLifecycleLead(orgId, leadId);
    },
    enabled: Boolean(orgId && leadId),
    staleTime: 10 * 1000,
  });
}

// ─── lifecycle: entity-action Edge Function mutations ─────────────────────
// All lead lifecycle actions route through the generalized `entity-action`
// function (entity_type:'lead'). Quote creation is NOT here — the QuoteWizard
// is the single quote creator (it advances the lead to 'quoted' itself).

async function invokeLeadAction<T>(action: string, leadId: string, extra: Record<string, unknown> = {}): Promise<T> {
  const result = await supabase.functions.invoke<T>('entity-action', {
    body: { action, entity_type: 'lead', entity_id: leadId, ...extra },
  });
  if (result.error) {
    const message = result.error instanceof Error ? result.error.message : 'entity-action invoke failed';
    throw new Error(message);
  }
  const data = result.data;
  if (!data) {
    throw new Error('entity-action returned empty response');
  }
  return data;
}

function invalidateLeadAndConversation(qc: ReturnType<typeof useQueryClient>, orgId: string | null) {
  void qc.invalidateQueries({ queryKey: ['lifecycle-lead', orgId] });
  void qc.invalidateQueries({ queryKey: ['leads', orgId] });
  void qc.invalidateQueries({ queryKey: ['bot-conversations', orgId] });
  void qc.invalidateQueries({ queryKey: ['bot-conversation', orgId] });
}

export function useChangeLeadStatus(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lead_id, to_status, note }: { lead_id: string; to_status: LifecycleLeadStatus; note?: string }) =>
      invokeLeadAction<LeadActionChangeStatusResponse>('change_status', lead_id, { to_status, note }),
    onSuccess: () => {
      invalidateLeadAndConversation(qc, orgId);
    },
  });
}

export function useConvertLeadToCustomer(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lead_id }: { lead_id: string }) =>
      invokeLeadAction<LeadActionConvertResponse>('convert_to_customer', lead_id),
    onSuccess: () => {
      invalidateLeadAndConversation(qc, orgId);
      void qc.invalidateQueries({ queryKey: ['companies', orgId] });
    },
  });
}

export function useAppendLeadNote(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lead_id, note }: { lead_id: string; note: string }) =>
      invokeLeadAction<LeadActionAppendNoteResponse>('append_note', lead_id, { note }),
    onSuccess: () => {
      invalidateLeadAndConversation(qc, orgId);
    },
  });
}

// ─── bot actions: takeover / release (semantic wrappers) ──────────────────
// Stay with the current row's casing so the website bot (v1 uppercase) and
// future v2 (lowercase) both see a state value they recognize. Route the
// existing useUpdateConversationState mutation under the hood.

function isV2Casing(state: BotState): boolean {
  return state !== state.toUpperCase();
}

export function useTakeoverConversation(orgId: string | null) {
  const update = useUpdateConversationState(orgId);
  return {
    ...update,
    mutateAsync: ({ id, currentState }: { id: string; currentState: BotState }) => {
      const target: BotState = isV2Casing(currentState) ? 'human-takeover' : 'HUMAN_TAKEOVER';
      return update.mutateAsync({ id, state: target });
    },
  };
}

export function useReleaseConversation(orgId: string | null) {
  const update = useUpdateConversationState(orgId);
  return {
    ...update,
    mutateAsync: ({ id, currentState }: { id: string; currentState: BotState }) => {
      const target: BotState = isV2Casing(currentState) ? 'idle-after-handoff' : 'IDLE_AFTER_HANDOFF';
      return update.mutateAsync({ id, state: target });
    },
  };
}
